import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import type { OrgDashboardTenant, OrgOpenOrder } from "../../../api/client.ts";
import { formatNumber } from "../../../utils/format.ts";
import FormattedDate from "../../FormattedDate.tsx";
import Panel from "../../Panel.tsx";
import StatusChip from "../../StatusChip.tsx";
import LiveDot from "../../LiveDot.tsx";

interface OpenOrdersPanelProps {
  orders: OrgOpenOrder[];
  tenants: Map<string, OrgDashboardTenant>;
  showTenant: boolean;
}

function isPast(deadline: string): boolean {
  // A date without a time: due at the end of that day, local time.
  return new Date(`${deadline}T23:59:59`).getTime() < Date.now();
}

/** The org's open prospect orders: how far each is, and whether an agent is on it. Hidden without any. */
export default function OpenOrdersPanel({ orders, tenants, showTenant }: OpenOrdersPanelProps) {
  if (orders.length === 0) return null;
  return (
    <Panel disablePadding title="Open orders" count={orders.length} subtitle="Prospect requests being worked">
      <Stack>
        {orders.map((order) => {
          const share = order.requested_count ? Math.min((order.delivered / order.requested_count) * 100, 100) : null;
          const late = order.deadline != null && isPast(order.deadline);
          return (
            <ButtonBase
              key={order.id}
              component={Link}
              to={`/organization/orders/${order.id}`}
              sx={{
                display: "block",
                textAlign: "left",
                px: 2,
                py: 1.25,
                borderBottom: 1,
                borderColor: "divider",
                "&:last-of-type": { borderBottom: 0 },
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0 }}>
                {order.agent_running && (
                  <Tooltip title="An agent is working on it">
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      <LiveDot />
                    </Box>
                  </Tooltip>
                )}
                <Typography variant="body2" noWrap sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                  {order.title}
                </Typography>
                <StatusChip kind="order" value={order.status} variant="outlined" />
              </Stack>
              {share !== null && (
                <LinearProgress
                  variant="determinate"
                  value={share}
                  color={share >= 100 ? "success" : "primary"}
                  sx={{ height: 4, borderRadius: 2, bgcolor: "action.hover", my: 0.75 }}
                />
              )}
              <Typography variant="caption" sx={{ color: "text.secondary", display: "flex", gap: 0.75, mt: share === null ? 0.25 : 0 }}>
                <span>
                  {formatNumber(order.delivered)}
                  {order.requested_count ? ` of ${formatNumber(order.requested_count)}` : ""} delivered
                </span>
                {showTenant && tenants.get(order.tenant_id) && <span>· {tenants.get(order.tenant_id)!.name}</span>}
                {order.deadline && (
                  <Box component="span" sx={{ color: late ? "warning.main" : undefined, ml: "auto" }}>
                    {late ? "Was due " : "Due "}
                    <FormattedDate value={order.deadline} />
                  </Box>
                )}
              </Typography>
            </ButtonBase>
          );
        })}
      </Stack>
    </Panel>
  );
}
